// The claim journey — the design drop's C-SIGNUP-1/2/3 + C-FINISH, reworked.
//
// Ported from the 2026-08-26 design drop,
// handoff/commerce-app/src/pages/AuthPages.tsx (`CreateCompanyPage`,
// `FinishSetupPage`). The card, the step rail, the agreement callout, the task
// list and the copy's voice are the handoff's. What changed is what the journey
// IS, and the changes are listed here rather than scattered as one-liners,
// because taken together they are the point of this file.
//
// REWORKED — the wizard was drawn as a SIGN-UP; the journey is a CLAIM.
//
// 1. There is no "create your company" step. The customer already exists: the
//    channel hand-off resolved the buyer and `record_subscriber` created the
//    company from what the channel called them. So the company name is shown
//    as a fact to check, not typed into an input — and typing one would create
//    a second company nobody bought anything under.
//
//    AMENDED 2026-09-10, and the amendment is narrower than it looks. The
//    company still is not CREATED here. But a channel sells to an ACCOUNT and
//    cannot tell us what the people there call themselves, so what it gave us
//    is a stand-in — and nothing else in the service could ever replace it:
//    there is no admin registration for Customer, ECS exec is off on the task,
//    and the roster's rename refuses any customer a channel has sold to. So
//    the journey asks, once, and only while the server says the name is still
//    a placeholder (`company_name_is_a_placeholder` on the waiting claim).
//    NAMING a company that exists is a different act from creating one, which
//    is why this amends the note above instead of reversing it.
//
// 1a. A returning buyer does not get a second company. If the person signing
//    in at step 2 already belongs to one, the server moves the waiting
//    subscription onto it and discards the placeholder — so the company shown
//    at step 1 is not necessarily the company they end up in. That is why the
//    finish screen prints `claimed.customer` (what came back from the claim)
//    and never `claim.customer` (what was waiting before it).
//
// 2. The steps are reordered. The handoff creates the account first and
//    confirms the subscription second. Here confirming has to come first,
//    because the account step is the one that performs the claim — a
// "confirm your subscription" screen after the subscription is already
//    claimed would be a screen asking about something already settled.
//
// 3. The decision-pack radio is gone from step 2. A pack is not chosen at claim
//    time: the agreement the channel sold is what it is, and the one place a
//    pack is named in this service's model is on a deployment request, which
//    happens after the claim. Offering the choice here would be offering a
//    choice nothing records.
//
// 4. The instance step is gone, and the "Provisioning prod · about 15 minutes"
//    row with it. Standing a platform up is people's work in this service —
// `ProvisioningState`'s own docstring says so — so the finish screen offers
//    to start that request rather than pretending a pipeline is already running.
//
// 5. "You'll be the company's first Billing Admin" is gone, and so are the
// "Invite your team" and "Set a usage alert" rows. There is no role on a
//    CustomerMembership, no invite flow, and no alerting. Each of those lines
//    describes a feature; none of them describes this service.
//
// 6. "Already set up? Sign in" toggles IN PLACE instead of navigating to the
//    sign-in screen. It has to: the hand-off lives in this browser's session
//    and the ordinary sign-in refuses an account that holds no membership, so
//    sending a returning buyer to /portal/login would refuse them and lose
//    what they came to claim.
import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, LogIn, ShoppingCart } from 'lucide-react';
import { Button } from '@miq/design-system/components/ui/button';
import { Checkbox } from '@miq/design-system/components/ui/checkbox';
import { Input } from '@miq/design-system/components/ui/input';
import { Label } from '@miq/design-system/components/ui/label';

import { AuthShell } from '../components/AuthShell';
import { StateBadge } from '../components/StateBadge';
import { usePortalSession } from '../auth/context';
import type { Customer, Subscription } from '../data/subscriptions';
import {
  ClaimRefused,
  claimWithExistingAccount,
  claimWithNewAccount,
  fetchWaitingClaim,
  type WaitingClaim,
} from './api';

/** Which screen of the journey is showing. */
type Step = 1 | 2 | 3;

/** ADAPTED: "Company / Subscription / Instance" — see REWORKED 1, 2 and 4. */
const STEPS = ['Subscription', 'Account', 'Done'] as const;

type Loading =
  | { status: 'reading' }
  | { status: 'waiting'; claim: WaitingClaim }
  | { status: 'nothing-waiting' }
  | { status: 'unreadable'; refusal: string };

/**
 * What the fulfillment endpoint says when it could not bring a purchase across.
 *
 * Kept in step with HANDOFF_NOT_COMPLETED in subscriptions/fulfillment.py: the
 * backend redirects a failed hand-off here with this in the query string, and
 * it is the only thing that distinguishes a buyer whose purchase did not make
 * it from a person who typed the address.
 */
const HANDOFF_FAILED = 'not-completed';

export interface ClaimJourneyProps {
  /** Where "Go to the portal" goes once the claim is done. */
  onFinished: () => void;
  /** Where the finish screen's deployment request goes, by subscription. */
  onRequestDeployment: (publicId: string) => void;
  /** The ordinary sign-in screen, for somebody who landed here with nothing. */
  onSignIn: () => void;
}

export function ClaimJourney({
  onFinished,
  onRequestDeployment,
  onSignIn,
}: ClaimJourneyProps) {
  const [searchParams] = useSearchParams();
  const handoffFailed = searchParams.get('handoff') === HANDOFF_FAILED;
  const [loading, setLoading] = useState<Loading>({ status: 'reading' });
  const [step, setStep] = useState<Step>(1);
  const [claimed, setClaimed] = useState<{
    subscription: Subscription;
    customer: Customer;
    email: string;
    isNewAccount: boolean;
  } | null>(null);

  useEffect(() => {
    // Nothing to read. The hand-off failed before anything was recorded, so
    // asking what is waiting would spend a request to be told what the
    // address already says — and would answer with the wandered-in screen.
    if (handoffFailed) {
      return;
    }

    let current = true;

    (async () => {
      try {
        const claim = await fetchWaitingClaim();
        if (current) {
          setLoading({ status: 'waiting', claim });
        }
      } catch (failure) {
        if (!(failure instanceof ClaimRefused)) {
          throw failure;
        }
        if (!current) {
          return;
        }
        // 404 is not an error here — it is what "you did not arrive from a
        // channel" looks like, and it is by far the most common way to reach
        // this address by hand.
        setLoading(
          failure.status === 404
            ? { status: 'nothing-waiting' }
            : { status: 'unreadable', refusal: failure.message },
        );
      }
    })();

    return () => {
      current = false;
    };
  }, [handoffFailed]);

  // Before every other state: this person was buying something and it did not
  // work, which is a different thing from having nothing to claim.
  if (handoffFailed) {
    return <HandoffNotCompleted />;
  }

  if (loading.status === 'reading') {
    return <div className="min-h-screen bg-muted/30" />;
  }

  if (loading.status === 'nothing-waiting') {
    return <NothingWaiting onSignIn={onSignIn} />;
  }

  if (loading.status === 'unreadable') {
    return (
      <AuthShell>
        <h1 className="text-xl font-semibold tracking-tight">
          We could not read what is waiting
        </h1>
        <p role="alert" className="mt-2 text-sm text-destructive">
          {loading.refusal}
        </p>
        <Button variant="outline" className="mt-5 w-full" onPress={onSignIn}>
          Go to sign in
        </Button>
      </AuthShell>
    );
  }

  const { claim } = loading;

  return (
    <AuthShell width={470}>
      <StepRail step={step} />
      {step === 1 && (
        <ConfirmSubscription claim={claim} onContinue={() => setStep(2)} />
      )}
      {step === 2 && (
        <TakeHold
          claim={claim}
          onBack={() => setStep(1)}
          onClaimed={(result) => {
            setClaimed(result);
            setStep(3);
          }}
        />
      )}
      {step === 3 && claimed && (
        <Finished
          claimed={claimed}
          onFinished={onFinished}
          onRequestDeployment={() =>
            onRequestDeployment(claimed.subscription.public_id)
          }
        />
      )}
    </AuthShell>
  );
}

/**
 * The step rail.
 *
 * ADAPTED: the handoff's rail lets you click back to any completed step. Step 3
 * here is reached by granting a membership, which is not a thing that can be
 * stepped back out of, so a completed step is shown as completed and is not a
 * button. The forward-only rail is the honest one for an irreversible act.
 */
function StepRail({ step }: { step: Step }) {
  return (
    <ol className="mb-5 flex gap-2">
      {STEPS.map((label, index) => {
        const n = (index + 1) as Step;
        const done = n < step;
        return (
          <li
            key={label}
            aria-current={n === step ? 'step' : undefined}
            className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${
              n === step
                ? 'border-link text-link'
                : done
                  ? 'border-success/30 bg-success-soft text-success'
                  : 'text-muted-foreground'
            }`}
          >
            <span className="flex size-4 items-center justify-center rounded-full bg-muted text-[10px]">
              {done ? <Check className="size-2.5" /> : n}
            </span>
            {label}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Step 1 — what the channel handed over.
 *
 * The handoff's `AgreementCallout` promoted from a reassurance strip on the
 * signup form to the step it deserves to be, because in a claim it is the thing
 * being claimed. Every value is the one the channel supplied; nothing on this
 * screen is an input, because nothing on it is ours to change.
 *
 * Including the company name, which the next step DOES ask for where it is
 * still a placeholder. The question belongs there and not here: this screen is
 * "is this your purchase?", and mixing a question we need answered into a
 * screen whose only job is confirmation is how a person ends up answering one
 * while reading the other. Here the placeholder is labelled as one, so nobody
 * is asked to confirm a name as theirs when it plainly is not.
 */
function ConfirmSubscription({
  claim,
  onContinue,
}: {
  claim: WaitingClaim;
  onContinue: () => void;
}) {
  const { subscription, customer } = claim;
  return (
    <>
      <h1 className="text-xl font-semibold tracking-tight">
        Confirm your subscription
      </h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Your purchase reached us. Check that this is you, then make an account to
        take hold of it.
      </p>
      <div className="mt-4 flex items-center gap-3 rounded-lg border bg-muted/40 px-3.5 py-3">
        <ShoppingCart className="size-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1 leading-tight">
          <div className="text-sm font-semibold">{customer.display_name}</div>
          <div className="truncate text-xs text-muted-foreground">
            {subscription.channel} ·{' '}
            <code className="font-mono">{subscription.agreement_ref}</code>
          </div>
        </div>
        <StateBadge subscription={subscription} />
      </div>
      <dl className="mt-4 space-y-1.5 rounded-lg border bg-muted/40 px-4 py-3 text-sm">
        <Fact label="Company">
          {claim.company_name_is_a_placeholder ? (
            <span className="text-muted-foreground">
              {customer.display_name} — we&rsquo;ll ask you next
            </span>
          ) : (
            customer.display_name
          )}
        </Fact>
        <Fact label="Sales channel">{subscription.channel}</Fact>
        <Fact label="Agreement">
          <code className="font-mono text-xs">{subscription.agreement_ref}</code>
        </Fact>
        <Fact label="Status">{subscription.state_label}</Fact>
      </dl>
      {/* ADAPTED: the handoff states pricing and term from its fixture. This
          service records neither on a subscription — the channel prices the
          agreement and bills it — so the screen says where that lives instead
          of printing numbers it would be making up. */}
      <p className="mt-3 text-xs text-muted-foreground">
        {claim.company_name_is_a_placeholder
          ? 'Your sales channel told us which account paid, not what your company is called — the next step asks. '
          : 'The company name came from your sales channel. '}
        Pricing, term and payment stay with the channel that sold this — tell us
        if any of the above is wrong before you continue.
      </p>
      <Button className="mt-5 w-full" onPress={onContinue}>
        This is us — continue
      </Button>
    </>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 truncate font-medium">{children}</dd>
    </div>
  );
}

/**
 * Step 2 — the act. Make an account, or sign in to the one they hold.
 *
 * Both submit paths end in the same place: a membership, a spent hand-off, and
 * a session. The toggle between them is in place rather than a link away, for
 * the reason at the head of this file.
 *
 * The company name rides on the same submit, both ways round, because it is
 * part of the same act: one post grants the membership and takes the name, in
 * one transaction on the server. A separate "name your company" screen after
 * the claim would be a screen somebody can close, and the company would then
 * keep a placeholder name that nothing else in the service can change.
 */
function TakeHold({
  claim,
  onBack,
  onClaimed,
}: {
  claim: WaitingClaim;
  onBack: () => void;
  onClaimed: (result: {
    subscription: Subscription;
    customer: Customer;
    email: string;
    isNewAccount: boolean;
  }) => void;
}) {
  const { adoptSession } = usePortalSession();
  const [returning, setReturning] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [password, setPassword] = useState('');
  const [keepSignedIn, setKeepSignedIn] = useState(true);
  const [refusal, setRefusal] = useState('');
  const [problems, setProblems] = useState<Record<string, string[]>>({});
  const [submitting, setSubmitting] = useState(false);

  // Asked only while the server says it is still open to answer. Sending a
  // name for a company that has one is harmless — the server ignores it — but
  // ASKING for one would be the harm: it would invite somebody to rename a
  // company that already has colleagues in it, and then quietly not.
  const asksForCompanyName = claim.company_name_is_a_placeholder;

  const submit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      setRefusal('');
      setProblems({});
      setSubmitting(true);
      try {
        const answered = asksForCompanyName ? companyName : '';
        const result = returning
          ? await claimWithExistingAccount({
              email,
              password,
              companyName: answered,
              keepSignedIn,
            })
          : await claimWithNewAccount({
              email,
              password,
              name,
              companyName: answered,
              keepSignedIn,
            });

        // The session before the navigation: the finish screen's actions lead
        // into the portal, and arriving there without a session would bounce
        // straight back to sign-in.
        await adoptSession(result.accessToken);
        onClaimed({
          subscription: result.subscription,
          customer: result.customer,
          email,
          isNewAccount: !returning,
        });
      } catch (failure) {
        if (!(failure instanceof ClaimRefused)) {
          throw failure;
        }
        if (failure.alreadyRegistered) {
          // Not a correction to this form — the other door. Switch to it and
          // keep the address they typed, so the only thing left to do is the
          // password.
          setReturning(true);
          setPassword('');
          setRefusal(failure.message);
        } else {
          setRefusal(failure.message);
          setProblems(failure.problems);
        }
      } finally {
        setSubmitting(false);
      }
    },
    [
      adoptSession,
      asksForCompanyName,
      companyName,
      email,
      keepSignedIn,
      name,
      onClaimed,
      password,
      returning,
    ],
  );

  return (
    <>
      <h1 className="text-xl font-semibold tracking-tight">
        {returning ? 'Sign in to claim it' : 'Create your account'}
      </h1>
      <p className="mt-1 text-sm text-muted-foreground">
        {/* The company is named by its display name only where that name came
            from a person. Printing a placeholder in a possessive — "AWS
            account 4120's deployments" — reads as though we believe it, and
            the whole reason the next field exists is that we do not. */}
        {asksForCompanyName
          ? returning
            ? 'Sign in and this subscription becomes yours to manage.'
            : 'Your team signs in with accounts like this one to manage your deployments, metering and billing.'
          : returning
            ? `Sign in and ${claim.customer.display_name}'s subscription becomes yours to manage.`
            : `Your team signs in with accounts like this one to manage ${claim.customer.display_name}'s deployments, metering and billing.`}
      </p>
      <form onSubmit={submit} noValidate>
        <div className="mt-4 space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="claim-email">Work email</Label>
            <Input
              id="claim-email"
              name="email"
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <FieldProblems problems={problems.email} />
          </div>
          {/* ADAPTED: the handoff pairs "Your name" with a "Company name"
              input. The person's name is optional because this pool has never
              required one — it is stored as given, and an empty one shows as
              the address. */}
          {!returning && (
            <div className="space-y-1.5">
              <Label htmlFor="claim-name">Your name (optional)</Label>
              <Input
                id="claim-name"
                name="name"
                autoComplete="name"
                placeholder="How your colleagues will see you"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
              <FieldProblems problems={problems.full_name} />
            </div>
          )}
          {/* Shown at BOTH doors, unlike the field above. Which door somebody
              came through says who they are; it says nothing about whether
              anybody has named their company yet, and the returning buyer with
              an account and no company is exactly the person this asks. */}
          {asksForCompanyName && (
            <div className="space-y-1.5">
              <Label htmlFor="claim-company">Company name (optional)</Label>
              <Input
                id="claim-company"
                name="company_name"
                autoComplete="organization"
                placeholder="What your company is called"
                value={companyName}
                onChange={(event) => setCompanyName(event.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Your sales channel could only tell us which account paid. Leave
                this empty and we will keep {claim.customer.display_name}.
              </p>
              <FieldProblems problems={problems.company_name} />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="claim-password">Password</Label>
            <Input
              id="claim-password"
              name="password"
              type="password"
              autoComplete={returning ? 'current-password' : 'new-password'}
              placeholder={returning ? '••••••••••' : '12+ characters'}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <FieldProblems problems={problems.password1} />
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox isSelected={keepSignedIn} onChange={setKeepSignedIn} /> Keep me
            signed in
          </label>
          {returning && (
            <a
              href="/accounts/password/reset/"
              className="text-sm text-link underline-offset-4 hover:underline"
            >
              Forgot password?
            </a>
          )}
        </div>
        {refusal && (
          <p role="alert" className="mt-3 text-sm text-destructive">
            {refusal}
          </p>
        )}
        <div className="mt-5 flex gap-2.5">
          <Button variant="outline" onPress={onBack} isDisabled={submitting}>
            Back
          </Button>
          <Button type="submit" className="flex-1" isPending={submitting}>
            {submitting
              ? 'Claiming…'
              : returning
                ? 'Sign in and claim'
                : 'Create account and claim'}
          </Button>
        </div>
      </form>
      <p className="mt-4 text-center text-sm text-muted-foreground">
        {returning ? 'No account with us yet? ' : 'Already have an account with us? '}
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0"
          onPress={() => {
            setReturning(!returning);
            setRefusal('');
            setProblems({});
          }}
        >
          {returning ? 'Create one' : 'Sign in instead'}
        </Button>
      </p>
    </>
  );
}

/** Whatever the server said about one field, shown where the field is. */
function FieldProblems({ problems }: { problems?: string[] }) {
  if (!problems?.length) {
    return null;
  }
  return (
    <ul className="space-y-0.5 text-xs text-destructive">
      {problems.map((problem) => (
        <li key={problem}>{problem}</li>
      ))}
    </ul>
  );
}

/**
 * Step 3 — C-FINISH, holding only what is true.
 *
 * The handoff's six task rows become three. The two that went are described at
 * the head of this file; the fake "provisioning, about 15 minutes" row became
 * the one thing left to do, which is to ask.
 */
function Finished({
  claimed,
  onFinished,
  onRequestDeployment,
}: {
  claimed: {
    subscription: Subscription;
    customer: Customer;
    email: string;
    isNewAccount: boolean;
  };
  onFinished: () => void;
  onRequestDeployment: () => void;
}) {
  const { subscription, customer, email, isNewAccount } = claimed;
  return (
    <>
      <div className="mx-auto mb-4 flex size-11 items-center justify-center rounded-full bg-success-soft text-success">
        <Check className="size-5" />
      </div>
      <h1 className="text-center text-xl font-semibold tracking-tight">
        {customer.display_name} is yours to manage
      </h1>
      <p className="mt-1 text-center text-sm text-muted-foreground">
        The subscription is linked to your account. One thing is left, and it is
        optional.
      </p>
      <ul className="mt-4">
        <TaskRow
          state="done"
          title="Subscription claimed"
          desc={
            <>
              {subscription.channel} ·{' '}
              <code className="font-mono">{subscription.agreement_ref}</code> ·{' '}
              {subscription.state_label}
            </>
          }
        />
        <TaskRow
          state="done"
          title={isNewAccount ? 'Account created' : 'Account linked'}
          desc={<>{email} — this is how you sign in from now on</>}
        />
        <TaskRow
          state="todo"
          title="Ask us for a deployment"
          desc="Tell us the region, a name and which decision pack, and our team stands it up."
          action={
            <Button variant="outline" size="sm" onPress={onRequestDeployment}>
              Request
            </Button>
          }
        />
      </ul>
      <Button className="mt-5 w-full" onPress={onFinished}>
        Go to the portal
      </Button>
      <p className="mt-4 text-center text-xs text-muted-foreground">
        Metering starts when a deployment first reports usage — nothing is
        metered until then.
      </p>
    </>
  );
}

function TaskRow({
  state,
  title,
  desc,
  action,
}: {
  state: 'done' | 'todo';
  title: string;
  desc: ReactNode;
  action?: ReactNode;
}) {
  return (
    <li className="flex items-start gap-3 border-b border-dashed py-2.5 last:border-b-0">
      <span
        className={`mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border ${
          state === 'done' ? 'border-success/30 bg-success-soft text-success' : ''
        }`}
      >
        {state === 'done' && <Check className="size-3" />}
      </span>
      <span className="min-w-0 flex-1">
        <span
          className={`block text-sm ${
            state === 'todo' ? 'font-medium text-muted-foreground' : 'font-semibold'
          }`}
        >
          {title}
        </span>
        <span className="block text-xs leading-snug text-muted-foreground">{desc}</span>
      </span>
      {action}
    </li>
  );
}

/**
 * Somebody reached the claim address without a hand-off.
 *
 * Which is what happens to anyone who types it, and to a buyer whose session
 * expired between the channel's redirect and here. It says the one thing that
 * is useful — go back through your marketplace, or sign in — and does not offer
 * a form, because there is nothing a form could supply. The claim never takes
 * a subscription identifier from the browser.
 */
/**
 * The hand-off did not complete.
 *
 * Replaces subscriptions/templates/subscriptions/handoff_not_completed.html,
 * this service's last server-rendered customer page, and keeps its two
 * promises: say that nothing was charged for the attempt, and say what to do
 * next. What went wrong is not here — it is in our logs — because the person
 * reading this might be the buyer and might be someone probing the endpoint.
 *
 * Deliberately not NothingWaiting below. That screen is for a person who
 * arrived at the claim address without a purchase behind them, and it reads
 * as an explanation of what the page is for. This one is for a person who has
 * just paid and needs to hear that this attempt cost them nothing, which is
 * the sentence they came for and the one the template led with.
 */
function HandoffNotCompleted() {
  return (
    <AuthShell>
      <h1 className="text-xl font-semibold tracking-tight">
        We could not complete this
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Something went wrong bringing your subscription across.{' '}
        <span className="font-medium text-foreground">
          Nothing has been charged for this attempt.
        </span>
      </p>
      <p className="mt-3 text-sm text-muted-foreground">
        Start again from your subscription on the marketplace you bought it
        from. If it happens twice, write to{' '}
        <a
          className="underline hover:text-foreground"
          href="mailto:mDecideSupport@metricsiq.ai"
        >
          mDecideSupport@metricsiq.ai
        </a>{' '}
        and we will pick it up by hand.
      </p>
    </AuthShell>
  );
}


function NothingWaiting({ onSignIn }: { onSignIn: () => void }) {
  return (
    <AuthShell>
      <h1 className="text-xl font-semibold tracking-tight">
        Nothing is waiting to be claimed
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        This page appears when a sales channel hands your purchase over to us. If
        you have just subscribed, open the subscription from your marketplace
        console again. If you already have an account with us, sign in.
      </p>
      <Button className="mt-5 w-full" onPress={onSignIn}>
        <LogIn className="size-4" /> Sign in
      </Button>
    </AuthShell>
  );
}
